import { Button, TextField, Autocomplete, IconButton, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CloseIcon from "@mui/icons-material/Close";
import {
  AdminItemsRow,
  AdminItemsContainer,
  AdminItemContainer,
  AdminInviteDetailsContainer,
  AdminItemContainerForm,
  AdminItemContainerChip,
} from "../../admin.styled";
import { Invite, Provider } from "../../admin.type";
import { useState } from "react";
import { Toast } from "core/components/global/toast/toast";
import { deleteGroup } from "modules/agentMySecurity/services/embeddingsService";
import { ConfirmationDialog } from "core/components/global/confirmationDialogBox/confirmationDialog";
import { formatDate } from "core/lib/utils";
import { useContext, useEffect } from "react";


export default function AdminGroup({
  group,
  handleUpdateGroup,
  availableProviders,
  onDelete
}) {
    const [openRow, setRowOpen] = useState({});
    const [description, setDescription] = useState<string>(group.description);
    const [name, setName] = useState<string>(group.name);
    const [providers, setProviders] = useState<Provider[]>(group.providers);
    const [openDialog, setOpenDialog] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleSetRowOpen = (id) => {
        if (openRow.hasOwnProperty(id)) {
            setRowOpen((prevState) => ({
                ...prevState,
                [id]: !prevState[id],
            }));
        } else {
            setRowOpen((prevState) => ({
                ...prevState,
                [id]: true,
            }));
        }
    };

    const preHandleUpdateInvite = () => {
        // Extract just the provider names
        const providerNames = providers.map(provider => provider?.name).filter(Boolean);
        
        handleUpdateGroup({
            _id: group._id,
            providers: providerNames, // Send only the provider names
            name: name,
            description: description
        })
    }

    // Locally update the list of invites
    const handleInviteChange = (currentInvite: Invite, field: string, value: any) => {
        if (field === "description") {
            setDescription(value);
        } else if (field === "name") {
            setName(value);
        } else if (field === "providers") {
            setProviders(value);
        }
    };

    const handleRemoveGroup = (providerName: string) => {
        setProviders(providers.filter((provider) => provider.name !== providerName))
    }

    const handleDeleteClick = () => {
        setOpenDialog(true);
    };
    
    const handleDelete = async () => {
    try {
        setIsDeleting(true);
        const response = await deleteGroup(group._id);
        if (response !== null) {
            onDelete(group._id);
            Toast("Invite deleted successfully", "success");
        } else {
            Toast("Failed to delete invite", "error");
        }
    } catch (error) {
        console.error("Delete error:", error);
        Toast("Error deleting invite", "error");
    } finally {
        setIsDeleting(false);
        setOpenDialog(false);
    }
    };

    return (
        <AdminItemsRow>
            <div className="unexpanded-row-container">
                <div className="row-items" onClick={() => handleSetRowOpen(group._id)}>
                    <p className="row-item">{group.name}</p>
                    <p className="row-item">{group.description}</p>
                    <p className="row-item">{formatDate(group.createdAt)}</p>
                </div>
                <div className="row-buttons">
                    <IconButton onClick={handleDeleteClick} disabled={isDeleting}>
                        <DeleteOutlineOutlinedIcon fontSize="small" color="error" />
                    </IconButton>
                </div>
            </div>

            {openRow[group._id] && (
                <div className="p-3">
                    <AdminItemsContainer>
                        <AdminInviteDetailsContainer>
                            <AdminItemContainer>
                                <strong>Name:</strong>
                                <TextField
                                    value={name}
                                    onChange={(e) =>
                                        handleInviteChange(group, "name", e.target.value)
                                    }
                                    variant="outlined"
                                    fullWidth
                                />
                            </AdminItemContainer>
                            <AdminItemContainer>
                                <strong>Description:</strong>
                                <TextField
                                    value={description}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value.length <= 500) {
                                        handleInviteChange(group, "description", value);
                                        }
                                    }}
                                    variant="outlined"
                                    fullWidth
                                    />
                            </AdminItemContainer>
                            <AdminItemContainer>
                                <strong>Groups:</strong>
                                <AdminItemContainerForm fullWidth>
                                    <Autocomplete
                                        multiple
                                        value={providers}
                                        onChange={(event, newProviders) => handleInviteChange(group, "providers", newProviders)}
                                        options={availableProviders ? [...availableProviders].sort((a, b) => a.name.localeCompare(b.name)) : []}
                                        getOptionLabel={(option) => option.name}
                                        isOptionEqualToValue={(option, value) => option === value}
                                        disableCloseOnSelect
                                        limitTags={3}
                                        filterSelectedOptions
                                        renderTags={(inputed) =>
                                            <>
                                                { availableProviders ? (
                                                    inputed.map((option, index) => (
                                                        option && (
                                                            <AdminItemContainerChip
                                                                className="m-1"
                                                                label={option.name}
                                                                key={`invite-group-list-index-${option._id}`}
                                                                onDelete={() => handleRemoveGroup(option.name)}
                                                                deleteIcon={<CloseIcon />}
                                                                variant="outlined"
                                                            />
                                                        )
                                                    ))
                                                ) : (
                                                    <p>Unable to fetch provider information at this time</p>
                                                )}
                                            </>
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                className="custom-input"
                                                variant="outlined"
                                                placeholder="Type to search providers"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <InputAdornment position="start">
                                                                <SearchIcon />
                                                            </InputAdornment>
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </AdminItemContainerForm>
                            </AdminItemContainer>
                        </AdminInviteDetailsContainer>
                        <Button
                            color="primary"
                            onClick={() => preHandleUpdateInvite()}
                            variant="outlined"
                        >
                            Update Group
                        </Button>
                    </AdminItemsContainer>
                </div>
            )}
            <ConfirmationDialog
                open={openDialog}
                title="Confirm Delete"
                message={`Are you sure you want to delete the Group ${group.name}?`}
                isLoading={isDeleting}
                onClose={() => setOpenDialog(false)}
                onConfirm={handleDelete}
            />
        </AdminItemsRow>
    );
}
